import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from "@carbon/react";
import Slide from "../../components/UI/Slide/Slide";
import { FileReader, TagBadge } from "../../components";
import { PurchaseRequestItemV2 } from "../../types";
import { IT_DOESNT_HAVE_SPEC, TO_BE_AGREED } from "../../utils";
import "./purchaseRequestsV2.scss";

export interface PurchaseRequestItemInfoPanelProps {
  item: PurchaseRequestItemV2 | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  langEs: boolean;
}

/**
 * The items-view "material info" slide-over — Task 4 Carbon migration of the pre-Carbon screen's
 * Tailwind `<dl>`. Reuses the shared `Slide` (a generic, pre-existing panel — out of this task's
 * owned-file scope, same as `Materials`/`Suppliers` reusing `NetworkError` verbatim) but rebuilds
 * its OWNED content on Carbon's `StructuredList` (label/value row pairs) instead of raw Tailwind
 * markup. Every field, and the order it renders in, is unchanged from the pre-Carbon panel.
 */
const PurchaseRequestItemInfoPanel = ({
  item,
  open,
  setOpen,
  langEs,
}: PurchaseRequestItemInfoPanelProps) => {
  const { t } = useTranslation();
  const material = item?.material;

  const row = (label: string, value: React.ReactNode) => (
    <StructuredListRow key={label}>
      <StructuredListCell className="purchase-requests__info-label">
        {label}
      </StructuredListCell>
      <StructuredListCell>{value}</StructuredListCell>
    </StructuredListRow>
  );

  return (
    <Slide open={open} setOpen={setOpen}>
      {material ? (
        <div className="purchase-requests__info-panel">
          <h3 className="purchase-requests__info-title">
            {t("purchaseRequests.items.materialInfo")}
          </h3>
          <StructuredListWrapper>
            <StructuredListBody>
              {row(
                t("purchaseRequests.items.columns.material"),
                `${material.ean ? `${material.ean} — ` : ""}${material.globalMaterialName || "-"}`
              )}
              {row(
                t("material.merchancyCode"),
                langEs ? material.category?.name : material.category?.nameEn
              )}
              {material.measurementUnit
                ? row(t("material.measurementUnit"), material.measurementUnit)
                : null}
              {material.deliveryFormat
                ? row(t("material.deliveryFormat"), material.deliveryFormat)
                : null}
              {row(
                t("material.deliveryPlace"),
                material.deliveryPlace?.name ?? TO_BE_AGREED
              )}
              {material.certifications && material.certifications.length > 0
                ? row(
                    t("material.certifications"),
                    material.certifications.map((cert) => cert.name).join(", ")
                  )
                : null}
              {material.tags && material.tags.length > 0
                ? row(
                    t("tags.tags", { defaultValue: "Tags" }),
                    <div className="purchase-requests__info-tags">
                      {material.tags.map((tag) => (
                        <TagBadge tag={tag} size="sm" key={tag.id} />
                      ))}
                    </div>
                  )
                : null}
              {material.brand
                ? row(
                    t("material.brand"),
                    <span className="purchase-requests__info-preformatted">
                      {material.brand}
                    </span>
                  )
                : null}
              {material.comments
                ? row(
                    t("material.comments"),
                    <span className="purchase-requests__info-preformatted">
                      {material.comments}
                    </span>
                  )
                : null}
              {material.details
                ? row(
                    t("newMaterialForm.details"),
                    <span className="purchase-requests__info-preformatted">
                      {material.details}
                    </span>
                  )
                : null}
              {row(
                t("material.confidentMaterial"),
                material.confident ? t("cta.yes") : t("cta.no")
              )}
              {row(
                t("material.confidential"),
                material.confidentSpec ? t("cta.yes") : t("cta.no")
              )}
              {row(
                t("material.specification"),
                material.fileURL ? (
                  <FileReader fileKey={material.fileURL} />
                ) : (
                  IT_DOESNT_HAVE_SPEC
                )
              )}
              {material.owner?.fullName
                ? row(t("material.responsible"), material.owner.fullName)
                : null}
              {material.user?.fullName
                ? row(t("material.owner"), material.user.fullName)
                : null}
            </StructuredListBody>
          </StructuredListWrapper>
        </div>
      ) : null}
    </Slide>
  );
};

export default PurchaseRequestItemInfoPanel;
